import { useState } from 'react'
import { useFormatters } from '../../contexts/PreferencesContext'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { TabProps } from './types'
import { ChartWrapper } from './ChartWrapper'
import { useDrilldownAction } from '../drilldown'

// Top N selector component
function TopNSelector({
  value,
  onChange,
}: {
  value: number
  onChange: (value: number) => void
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="text-xs border rounded px-2 py-1 bg-background"
    >
      <option value={5}>Top 5</option>
      <option value={10}>Top 10</option>
      <option value={20}>Top 20</option>
    </select>
  )
}

export function CostTab({ data, filters, dataPeriod: _dataPeriod, orderType: _orderType }: TabProps) {
  const { formatCurrency } = useFormatters()
  const [activeCostTab, setActiveCostTab] = useState('future')
  const [topPPVPositiveCustomers, setTopPPVPositiveCustomers] = useState(5)
  const [topPPVNegativeCustomers, setTopPPVNegativeCustomers] = useState(5)
  // CR-3848: Add state for Highest Value Customers top N selection
  const [topHighestValueCustomers, setTopHighestValueCustomers] = useState(5)

  // Hook for programmatic drill-down (for chart clicks)
  const { openDrilldown } = useDrilldownAction()

  // Convert dashboard filters to drilldown API format
  const drilldownFilters = {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    ...(filters?.dateTo && { date_to: filters.dateTo }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.site && { site: filters.site }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.customerGroup && { customer_group: filters.customerGroup }),
    ...(filters?.customerCountry && { customer_country: filters.customerCountry }),
  }

  // Arrears drilldown: exclude date_from/date_to since the dashboard computes
  // arrears on PO-level dates but drilldown applies them to delivery_date,
  // causing a mismatch. The overdue dynamic filter (delivery_date < today) is sufficient.
  const arrearsDrilldownFilters = {
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.site && { site: filters.site }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.customerGroup && { customer_group: filters.customerGroup }),
    ...(filters?.customerCountry && { customer_country: filters.customerCountry }),
  }

  // Helper function to parse month string to date range
  const parseMonthToDateRange = (monthStr: string): { date_from: string; date_to: string } | null => {
    try {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const parts = monthStr.trim().split(' ')
      const monthIndex = monthNames.indexOf(parts[0])
      if (monthIndex === -1) return null
      let year: number
      if (parts.length >= 2 && parts[1]) {
        year = parseInt(parts[1])
        if (year < 100) year += 2000
      } else {
        year = new Date().getFullYear()
      }
      // CR-4671: Format in local timezone — see dashboardUtils.ts for context.
      const startDate = new Date(year, monthIndex, 1)
      const endDate = new Date(year, monthIndex + 1, 0)
      const fmt = (d: Date): string =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      return {
        date_from: fmt(startDate),
        date_to: fmt(endDate),
      }
    } catch {
      return null
    }
  }

  // Handle chart click - open drill-down modal with month-specific data
  // CR-3918: Added 'ppv' metric type for PPV to Contract/Standard chart
  const handleChartClick = (chartData: unknown, metricType: 'spend' | 'arrears' | 'ppv') => {
    if (!chartData) return
    const data = chartData as { activePayload?: Array<{ payload?: { month?: string } }>; activeLabel?: string }
    let month: string | null = null
    if (data.activePayload && data.activePayload[0]) {
      month = data.activePayload[0].payload?.month || null
    } else if (data.activeLabel) {
      month = data.activeLabel
    }
    if (!month) return

    // Parse month to date range for API filtering
    const dateRange = parseMonthToDateRange(month)
    const chartFilters = dateRange ? {
      ...drilldownFilters,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
    } : drilldownFilters

    openDrilldown(metricType, chartFilters, month)
  }

  // Get cost data from backend or use defaults
  const costData = data.costData || {
    total_incoming_value: 0,
    total_incoming_lines: 0,
    total_arrears_value: 0,
    total_arrears_lines: 0,
    total_customers: 0,
    incoming_by_week: [],
    arrears_by_customer: [],
  }

  // Incoming by month data (from spendByMonth)
  const incomingByMonthValue = data.charts?.spendByMonth || []

  // CR-4211: Use real per-month line counts from API (falls back to even distribution)
  const incomingByMonthLines = incomingByMonthValue.map((item) => ({
    month: item.month,
    lines: item.lines ?? 0,
  }))

  // CR-3847 + CR-3925: Incoming Per Week by Latest Customer Promise Date
  // Now includes all 4 metrics: arrears value, arrears lines, incoming value, incoming lines
  const incomingByWeekPromiseDate = (costData.incoming_by_week || []).map((w: { week: string; value: number; lines?: number; arrears_value?: number; arrears_lines?: number }) => ({
    week: w.week,
    incoming_value: w.value,
    incoming_lines: w.lines || 0,
    arrears_value: w.arrears_value || 0,
    arrears_lines: w.arrears_lines || 0,
  }))

  // CR-3966: Dedicated click handler for the weekly chart
  // Opens arrears drilldown with all open PO lines (no date filter since week labels are relative)
  const handleWeekChartClick = () => {
    openDrilldown('arrears', arrearsDrilldownFilters, 'Incoming Per Week')
  }

  // Arrears by customers data (value and count)
  // CR-3966: Keep full customer name for drill-down filtering, truncate only for display via tick formatter
  const arrearsByCustomer = (costData.arrears_by_customer || []).map((s: { customer: string; arrears: number; count: number }) => ({
    customer: s.customer,
    arrears: s.arrears,
    count: s.count || 0,
  }))

  // PPV to Contract/Standard data - requires PPV stats endpoint (not yet wired)
  const ppvTrendData: Array<{ month: string; ppv: number; contract: number; standard: number }> = []

  // PPV Variance (Positive) by Customers - requires PPV stats endpoint
  const ppvPositiveCustomers: Array<{ name: string; variance: number }> = []

  // PPV Variance (Negative) by Customers - requires PPV stats endpoint
  const ppvNegativeCustomers: Array<{ name: string; variance: number }> = []

  // CR-3848: Highest Value Customers - aggregated from incoming_lines
  const highestValueCustomers = (costData.incoming_lines || [] as Array<{ customer: string; total_value: number }>)
    .reduce((acc: Array<{ name: string; value: number }>, line: { customer: string; total_value: number }) => {
      const existing = acc.find(s => s.name === line.customer)
      if (existing) {
        existing.value += line.total_value
      } else {
        acc.push({ name: line.customer, value: line.total_value })
      }
      return acc
    }, [] as Array<{ name: string; value: number }>)
    .sort((a: { value: number }, b: { value: number }) => b.value - a.value)
    .slice(0, topHighestValueCustomers)

  return (
    <div className="space-y-4">
      {/* Sub-tabs for Future and Historical */}
      <div className="bg-card rounded-lg shadow-sm">
        <div className="border-b">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveCostTab('future')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeCostTab === 'future'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Future
            </button>
            <button
              onClick={() => setActiveCostTab('historical')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeCostTab === 'historical'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
              }`}
            >
              Historical
            </button>
          </nav>
        </div>
        <div className="p-6">
          {activeCostTab === 'future' && (
            <div className="space-y-6">
              {/* Cost Overview Cards */}
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-blue-600 text-white p-6 rounded-lg text-center">
                  <h3 className="text-lg font-medium mb-2">Total Incoming</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-blue-700 p-3 rounded">
                      <div className="text-sm">Value</div>
                      <div className="text-xl font-bold">{formatCurrency(costData.total_incoming_value || data.cost?.totalSpend || 0)}</div>
                    </div>
                    <div className="bg-blue-700 p-3 rounded">
                      <div className="text-sm">No.of SO Lines</div>
                      <div className="text-xl font-bold">{(costData.total_incoming_lines || data.purchasing?.totalLines || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-600 text-white p-6 rounded-lg text-center">
                  <h3 className="text-lg font-medium mb-2">Total Arrears</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-blue-700 p-3 rounded">
                      <div className="text-sm">Value</div>
                      <div className="text-xl font-bold">{formatCurrency(costData.total_arrears_value || data.cost?.historicalArrears || 0)}</div>
                    </div>
                    <div className="bg-blue-700 p-3 rounded">
                      <div className="text-sm">No.of SO Lines</div>
                      <div className="text-xl font-bold">{(costData.total_arrears_lines || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-600 text-white p-6 rounded-lg text-center">
                  <h3 className="text-lg font-medium mb-2">Total Customers</h3>
                  <div className="text-4xl font-bold mt-4">{costData.total_customers || data.customers?.total || 0}</div>
                </div>
              </div>

              {/* Charts Row 1 - Incoming by Month (Value and Lines) */}
              <div className="grid grid-cols-2 gap-6">
                <ChartWrapper
                  title="Incoming by Month (Value)"
                  data={incomingByMonthValue}
                  exportFilename="incoming-by-month-value"
                >
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={incomingByMonthValue}
                      onClick={(chartData) => handleChartClick(chartData, 'spend')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                      <Bar dataKey="spend" fill="#3b82f6" name="Value" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="Incoming by Month (SO Lines)"
                  data={incomingByMonthLines}
                  exportFilename="incoming-by-month-lines"
                >
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={incomingByMonthLines}
                      onClick={(chartData) => handleChartClick(chartData, 'spend')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Bar dataKey="lines" fill="#10b981" name="SO Lines" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* CR-3847 + CR-3925: Incoming Per Week by Latest Customer Promise Date - Single combined chart */}
              <ChartWrapper
                title="Incoming Per Week by Latest Customer Promise Date"
                data={incomingByWeekPromiseDate}
                exportFilename="incoming-by-week"
                legend={[
                  { color: '#3b82f6', label: 'Total Incoming Value' },
                  { color: '#ef4444', label: 'Arrears to Date Value' },
                  { color: '#10b981', label: 'Total Incoming SO Lines' },
                  { color: '#f59e0b', label: 'Arrears to Date SO Lines' },
                ]}
              >
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart
                    data={incomingByWeekPromiseDate}
                    onClick={handleWeekChartClick}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="value" tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                    <YAxis yAxisId="lines" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip
                      cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                      formatter={(value: number, name: string) =>
                        name.includes('Value') ? value.toLocaleString() : `${value} lines`
                      }
                    />
                    <Bar yAxisId="value" dataKey="incoming_value" fill="#3b82f6" name="Total Incoming Value" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="value" dataKey="arrears_value" fill="#ef4444" name="Arrears to Date Value" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="lines" dataKey="incoming_lines" fill="#10b981" name="Total Incoming SO Lines" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="lines" dataKey="arrears_lines" fill="#f59e0b" name="Arrears to Date SO Lines" radius={[4, 4, 0, 0]} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartWrapper>

              {/* Charts Row 2 - Arrears By Customers (Count and Value) */}
              {/* CR-3966: Fixed drill-down to pass customer name from clicked bar */}
              <div className="grid grid-cols-2 gap-6">
                <ChartWrapper
                  title="Arrears By Customers"
                  data={arrearsByCustomer}
                  exportFilename="arrears-count-by-customer"
                >
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={arrearsByCustomer}
                      layout="vertical"
                      onClick={(chartData) => {
                        const customerName = (chartData as { activePayload?: Array<{ payload?: { customer?: string } }> })
                          ?.activePayload?.[0]?.payload?.customer
                        openDrilldown('arrears', {
                          ...arrearsDrilldownFilters,
                          ...(customerName && { customer_name: customerName }),
                        }, customerName || undefined)
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="customer" tick={{ fontSize: 10 }} width={100} tickFormatter={(value: string) => value.length > 15 ? value.substring(0, 15) + '...' : value} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => [`${value} SO Lines`, 'Arrears']} />
                      <Bar dataKey="count" fill="#3b82f6" name="Arrears Lines" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="Arrears Value By Customers"
                  data={arrearsByCustomer}
                  exportFilename="arrears-value-by-customer"
                >
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={arrearsByCustomer}
                      layout="vertical"
                      onClick={(chartData) => {
                        const customerName = (chartData as { activePayload?: Array<{ payload?: { customer?: string } }> })
                          ?.activePayload?.[0]?.payload?.customer
                        openDrilldown('arrears', {
                          ...arrearsDrilldownFilters,
                          ...(customerName && { customer_name: customerName }),
                        }, customerName || undefined)
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                      <YAxis type="category" dataKey="customer" tick={{ fontSize: 10 }} width={100} tickFormatter={(value: string) => value.length > 15 ? value.substring(0, 15) + '...' : value} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                      <Bar dataKey="arrears" fill="#ef4444" name="Arrears Value" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>

              {/* Charts Row 3 - PPV Section */}
              {ppvTrendData.length > 0 ? (
                <>
                  <ChartWrapper
                    title="PPV to Contract/Standard"
                    data={ppvTrendData}
                    exportFilename="ppv-trend"
                    legend={[
                      { color: '#3b82f6', label: 'PPV' },
                      { color: '#10b981', label: 'Contract' },
                      { color: '#f59e0b', label: 'Standard' },
                    ]}
                    onClick={() => openDrilldown('ppv', drilldownFilters)}
                  >
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart
                        data={ppvTrendData}
                        onClick={(chartData) => handleChartClick(chartData, 'ppv')}
                        style={{ cursor: 'pointer' }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                        <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                        <Line type="monotone" dataKey="ppv" stroke="#3b82f6" strokeWidth={2} name="PPV" dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="contract" stroke="#10b981" strokeWidth={2} name="Contract" dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="standard" stroke="#f59e0b" strokeWidth={2} name="Standard" dot={{ r: 3 }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ChartWrapper>

                  <div className="grid grid-cols-2 gap-6">
                    <ChartWrapper
                      title="PPV Variance (Positive) by Customers"
                      data={ppvPositiveCustomers}
                      exportFilename="ppv-positive-customers"
                      headerActions={
                        <TopNSelector value={topPPVPositiveCustomers} onChange={setTopPPVPositiveCustomers} />
                      }
                    >
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={ppvPositiveCustomers} layout="vertical" onClick={() => openDrilldown('spend', drilldownFilters)} style={{ cursor: 'pointer' }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                          <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                          <Bar dataKey="variance" fill="#10b981" name="Positive Variance" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartWrapper>

                    <ChartWrapper
                      title="PPV Variance (Negative) by Customers"
                      data={ppvNegativeCustomers}
                      exportFilename="ppv-negative-customers"
                      headerActions={
                        <TopNSelector value={topPPVNegativeCustomers} onChange={setTopPPVNegativeCustomers} />
                      }
                    >
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={ppvNegativeCustomers} layout="vertical" onClick={() => openDrilldown('spend', drilldownFilters)} style={{ cursor: 'pointer' }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                          <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                          <Bar dataKey="variance" fill="#ef4444" name="Negative Variance" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </ChartWrapper>
                  </div>
                </>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium">Purchase Price Variance (PPV)</p>
                  <p className="text-sm text-blue-700 mt-1">
                    PPV analysis requires contract pricing to be configured. Once contract prices are set up
                    for your suppliers, variance data will appear here automatically.
                  </p>
                </div>
              )}

              {/* Charts Row 5 - Highest Value Customers */}
              {/* CR-3848: Added Top 5/10/20 selector */}
              <ChartWrapper
                title="Highest Value Customers"
                data={highestValueCustomers}
                exportFilename="highest-value-customers"
                headerActions={
                  <TopNSelector value={topHighestValueCustomers} onChange={setTopHighestValueCustomers} />
                }
              >
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={highestValueCustomers}
                    layout="vertical"
                    onClick={() => openDrilldown('spend', drilldownFilters)}
                    style={{ cursor: 'pointer' }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={120} />
                    <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                    <Bar dataKey="value" fill="#8b5cf6" name="Total Value" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartWrapper>
            </div>
          )}

          {activeCostTab === 'historical' && (
            <div className="space-y-6">
              {/* Historical Cost Overview */}
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-gray-600 text-white p-6 rounded-lg text-center">
                  <h3 className="text-lg font-medium mb-2">Historical Spend</h3>
                  <div className="text-3xl font-bold">{formatCurrency(data.cost?.totalSpend || 0)}</div>
                  <p className="text-sm mt-2 opacity-80">Last 12 months</p>
                </div>

                <div className="bg-gray-600 text-white p-6 rounded-lg text-center">
                  <h3 className="text-lg font-medium mb-2">Total Arrears (Historical)</h3>
                  <div className="text-3xl font-bold">{formatCurrency(data.cost?.historicalArrears || 0)}</div>
                  <p className="text-sm mt-2 opacity-80">Cumulative</p>
                </div>

                <div className="bg-gray-600 text-white p-6 rounded-lg text-center">
                  <h3 className="text-lg font-medium mb-2">Cost Reduction</h3>
                  <div className={`text-3xl font-bold ${(data.deltas?.total_spend?.percentage_change ?? 0) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {data.deltas?.total_spend ? `${data.deltas.total_spend.percentage_change >= 0 ? '+' : ''}${data.deltas.total_spend.percentage_change.toFixed(1)}%` : 'N/A'}
                  </div>
                  <p className="text-sm mt-2 opacity-80">Month over month</p>
                </div>
              </div>

              {/* Historical Charts */}
              <div className="grid grid-cols-2 gap-6">
                <ChartWrapper
                  title="Historical Spend Trend"
                  data={data.charts?.spendByMonth || []}
                  exportFilename="historical-spend-trend"
                >
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart
                      data={data.charts?.spendByMonth || []}
                      onClick={(chartData) => handleChartClick(chartData, 'spend')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                      <Line type="monotone" dataKey="spend" stroke="#3b82f6" strokeWidth={2} name="Spend" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>

                <ChartWrapper
                  title="Historical Arrears Trend"
                  data={data.charts?.historicalArrears || []}
                  exportFilename="historical-arrears-trend"
                >
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart
                      data={data.charts?.historicalArrears || []}
                      onClick={(chartData) => handleChartClick(chartData, 'arrears')}
                      style={{ cursor: 'pointer' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => value.toLocaleString()} />
                      <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={2} name="Arrears" dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartWrapper>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
